import {useLocation, useHistory} from 'react-router-dom';
import queryparse from 'tricks/string/queryparse';
import querystringify from 'tricks/string/querystringify';

const SEPARATOR = '__';

function getModalQueryArray(modalQuery) {
	return modalQuery ? modalQuery.split(SEPARATOR) : [];
}

function getIsModalOpen(modalQuery, modalKey) {
	const modalQueryArray = getModalQueryArray(modalQuery);

	return modalQueryArray.includes(modalKey);
}

function addModalKeyToQuery(modalQuery, modalKey) {
	const modalQueryArray = getModalQueryArray(modalQuery);

	return [...modalQueryArray, modalKey].filter(Boolean).join(SEPARATOR);
}

function removeModalKeyFromQuery(modalQuery, modalKey) {
	const modalQueryArray = getModalQueryArray(modalQuery);
	const modalKeyIndex = modalQueryArray.findIndex(key => key === modalKey);

	// Remove the passed modal key, and any modal keys after it (nested modals).
	return modalQueryArray
		.slice(0, modalKeyIndex)
		.filter(Boolean)
		.join(SEPARATOR);
}

/**
 * Hook providing the state of a modal based on the URL query parameter 'modal'.
 * Returns a tuple `[isModalOpen, toggleModal]`.
 *
 * `isModalOpen` will be true when the modal key passed to the hook exists
 * in the query parameter, e.g. `?modal=share`.
 *
 * When multiple modals are open, modal names will be serialised and separated
 * by a double underscore e.g.:
 * `?modal=share__advanced-share__select-teams`.
 *
 * @param {string} modalKey - The name of the modal, as it will appear in the URL parameter.
 * @returns {Array} - State tuple. The first element is a Boolean `isModalOpen`, the second
 * is a function that will toggle the state of the modal.
 */

function useModalQueryParam(modalKey) {
	const location = useLocation();
	const history = useHistory();
	const {modal: modalQuery, ...otherQueryParams} = queryparse(
		location.search
	);

	if (!modalKey) {
		// eslint-disable-next-line no-console
		console.warn('useModalQueryParam: Missing parameter "modal key".');
	}

	if (modalKey.includes(SEPARATOR)) {
		// eslint-disable-next-line no-console
		console.warn(
			`useModalQueryParam: Invalid modal key '${modalKey}' – key must not contain the separator string '${SEPARATOR}'.`
		);
	}

	const isModalOpen = getIsModalOpen(modalQuery, modalKey);

	function updateModalQuery(newModalQuery) {
		const newQuery = querystringify(
			newModalQuery
				? {
						...otherQueryParams,
						modal: newModalQuery,
					}
				: otherQueryParams
		);

		history.replace({
			...location,
			search: `?${newQuery}`,
		});
	}

	function open() {
		updateModalQuery(addModalKeyToQuery(modalQuery, modalKey));
	}

	function close() {
		updateModalQuery(removeModalKeyFromQuery(modalQuery, modalKey));
	}

	function toggleModal() {
		isModalOpen ? close() : open();
	}

	return [isModalOpen, toggleModal];
}

export default useModalQueryParam;
